import React, { useEffect, useState } from 'react';
import { Box, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { addDays, avatarColor, dayLabel, dayTitle, today, weekStart, weekTitle } from '../labels';
import { Loader, MemberAvatar, useLoad } from '../common';
import { boardDays, boardItems } from './board';

/** How often the board reads again while it is on screen. */
const REFRESH_MS = 5 * 60 * 1000;

type Mode = 'day' | 'week';

/** The first day shown: today (or this week's Monday) moved by `offset` days or weeks, so it follows the clock past midnight. */
const firstDay = (mode: Mode, offset: number) =>
  mode === 'day' ? addDays(today(), offset) : addDays(weekStart(today()), offset * 7);

const columnColor = (column: T.CalendarBoardColumn) =>
  column.memberPublicId ? avatarColor({ name: column.name, color: column.color }) : '#757575';

/** One cell: a person's events and tasks on one day. */
function BoardCell({ column, day, large }: { column: T.CalendarBoardColumn; day: T.DateOnly; large: boolean }) {
  const items = boardItems(column, day);
  if (items.length === 0) return <Typography color="text.disabled" sx={{ fontSize: large ? 22 : 16, py: 0.5 }}>—</Typography>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: large ? 1 : 0.5 }}>
      {items.map(item => (
        <Box key={item.key} sx={{
          display: 'flex', gap: 1, alignItems: 'flex-start', p: large ? 1.25 : 0.75, borderRadius: 1.5,
          bgcolor: item.kind === 'event' ? 'action.hover' : 'transparent', opacity: item.isDone ? 0.55 : 1,
        }}>
          <Box sx={{ color: columnColor(column), display: 'flex', pt: large ? 0.5 : 0.25 }}>
            {item.kind === 'event'
              ? <EventIcon sx={{ fontSize: large ? 28 : 20 }} />
              : item.isDone
                ? <CheckCircleIcon sx={{ fontSize: large ? 28 : 20 }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: large ? 28 : 20 }} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
              fontSize: large ? 26 : 17, fontWeight: item.kind === 'event' ? 600 : 400, lineHeight: 1.25,
              textDecoration: item.isDone ? 'line-through' : 'none', overflowWrap: 'anywhere',
            }}>
              {item.title}
            </Typography>
            {(item.when || item.detail) && (
              <Typography color="text.secondary" sx={{ fontSize: large ? 20 : 14 }}>
                {[item.when, item.detail].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ColumnHeader({ column, large }: { column: T.CalendarBoardColumn; large: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, borderBottom: 4, borderColor: columnColor(column), minWidth: 0 }}>
      <MemberAvatar member={{ name: column.name, color: column.memberPublicId ? column.color : '#757575' }} size={large ? 48 : 36} />
      <Typography noWrap sx={{ fontSize: large ? 30 : 22, fontWeight: 600 }}>{column.name}</Typography>
    </Box>
  );
}

/**
 * The household board for a tablet or TV in a browser: one column per person plus "Casa", a day or a week, in large type.
 * Web only on purpose: on the phone the week view filtered by person does the same.
 */
const BoardPage: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('day');
  const [offset, setOffset] = useState(0);
  const [fullscreen, setFullscreen] = useState(!!document.fullscreenElement);
  const from = firstDay(mode, offset);
  const { data: board, error, loading, reload } = useLoad(
    () => api.getCalendarBoard(firstDay(mode, offset), mode === 'day' ? 1 : 7), [mode, offset]);

  // Every 5 minutes while on screen, and once when it comes back into view; nothing while hidden
  useEffect(() => {
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') reload(); }, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') reload(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [reload]);

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    else document.documentElement.requestFullscreen?.().catch(() => undefined);
  };

  const changeMode = (next: Mode | null) => {
    if (!next || next === mode) return;
    setMode(next);
    setOffset(0);
  };

  const title = mode === 'day' ? dayTitle(from) : `Semana de ${weekTitle(from)}`;
  const columns = board?.columns ?? [];
  const large = mode === 'day';

  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'background.default', display: 'flex', flexDirection: 'column', p: { xs: 1.5, md: 3 }, gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography component="h1" sx={{ fontSize: { xs: 26, md: 38 }, fontWeight: 700, flex: 1, minWidth: 200 }}>{title}</Typography>
        <ToggleButtonGroup exclusive size="large" value={mode} onChange={(_, value) => changeMode(value)} aria-label="Dia ou semana">
          <ToggleButton value="day">Dia</ToggleButton>
          <ToggleButton value="week">Semana</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title={mode === 'day' ? 'Dia anterior' : 'Semana anterior'}>
          <IconButton size="large" onClick={() => setOffset(o => o - 1)}><ChevronLeftIcon fontSize="large" /></IconButton>
        </Tooltip>
        <Tooltip title={mode === 'day' ? 'Hoje' : 'Esta semana'}>
          <span>
            <IconButton size="large" disabled={offset === 0} onClick={() => setOffset(0)}><TodayIcon fontSize="large" /></IconButton>
          </span>
        </Tooltip>
        <Tooltip title={mode === 'day' ? 'Dia seguinte' : 'Semana seguinte'}>
          <IconButton size="large" onClick={() => setOffset(o => o + 1)}><ChevronRightIcon fontSize="large" /></IconButton>
        </Tooltip>
        <Tooltip title={fullscreen ? 'Sair do ecrã inteiro' : 'Ecrã inteiro'}>
          <IconButton size="large" onClick={toggleFullscreen}>
            {fullscreen ? <FullscreenExitIcon fontSize="large" /> : <FullscreenIcon fontSize="large" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Fechar o painel">
          <IconButton size="large" onClick={() => navigate('/casa/semana')}><CloseIcon fontSize="large" /></IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Loader loading={loading} error={error} onRetry={reload} hasData={board !== undefined}>
          {mode === 'day' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(240px, 1fr))`, gap: 3, minHeight: '100%' }}>
              {columns.map(column => (
                <Box key={column.memberPublicId ?? 'casa'} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
                  <ColumnHeader column={column} large />
                  <BoardCell column={column} day={from} large={large} />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{
              display: 'grid', gridTemplateColumns: `120px repeat(${columns.length}, minmax(200px, 1fr))`,
              columnGap: 2, rowGap: 1, alignItems: 'start',
            }}>
              <Box />
              {columns.map(column => <ColumnHeader key={column.memberPublicId ?? 'casa'} column={column} large={false} />)}
              {boardDays(from, 7).map(day => (
                <React.Fragment key={day}>
                  <Box sx={{ pt: 0.75, borderTop: 1, borderColor: 'divider', alignSelf: 'stretch' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: day === today() ? 700 : 500, color: day === today() ? 'primary.main' : 'text.primary' }}>
                      {dayLabel(day, false)}
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 14 }}>{day === today() ? 'Hoje' : dayLabel(day).split(', ')[1]}</Typography>
                  </Box>
                  {columns.map(column => (
                    <Box key={`${day}-${column.memberPublicId ?? 'casa'}`} sx={{
                      pt: 0.75, borderTop: 1, borderColor: 'divider', alignSelf: 'stretch', minWidth: 0,
                      bgcolor: day === today() ? 'action.selected' : 'transparent',
                    }}>
                      <BoardCell column={column} day={day} large={false} />
                    </Box>
                  ))}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Loader>
      </Box>
    </Box>
  );
};

export default BoardPage;
